import { useMemo } from 'react';
import { Layout } from '../components/Layout';
import { LeaderboardTable, TeamLeaderboard, type TeamLeaderboardEntry } from '@aero-judge/ui';
import { browserPath, pilotProfilePath } from '../lib/api';
import { useResults, toLeaderboardEntries, useSlug } from '../hooks/useCompetition';

function CategoryPage({
  title,
  category,
  nameColumn,
  showBullseyes = true,
  showRoundScores = false,
  embedded = false,
}: {
  title: string;
  category: 'WOMEN' | 'TEAM' | 'COUNTRY';
  nameColumn: string;
  showBullseyes?: boolean;
  /** Individual boards: expand every official round with discard strikethrough */
  showRoundScores?: boolean;
  /** Render only the board, for the shared Results page. */
  embedded?: boolean;
}) {
  const competitionId = useSlug();
  const { data: results, isLoading } = useResults(category);
  const entries = toLeaderboardEntries(results);

  const teamEntries = useMemo((): TeamLeaderboardEntry[] => {
    if (category !== 'TEAM' || !results?.rankings) return [];
    return results.rankings
      .filter((row) => row.team)
      .map((row) => ({
        rank: row.rank,
        teamId: row.teamId ?? row.team!.id,
        name: row.team!.name,
        countryCode2: row.team!.country?.code2 || undefined,
        totalScoreCm: row.totalScoreCm,
        roundScores: (row.roundScores ?? []).map((rs) => ({
          round: rs.round,
          scoreCm: rs.scoreCm,
          isBullseye: rs.isBullseye,
          isDiscarded: rs.isDiscarded,
          isProvisional: rs.isProvisional,
        })),
        pilots: (row.pilots ?? []).map((p) => ({
          pilotId: p.pilotId,
          pilotNumber: p.pilotNumber,
          firstName: p.firstName,
          lastName: p.lastName,
          role: p.role,
          photoUrl: p.photoUrl ?? undefined,
          profileHref: (() => {
            const path = pilotProfilePath(results?.competition.id ?? competitionId, {
              pilotNumber: p.pilotNumber,
              aeroJudgeId: p.aeroJudgeId,
            });
            return path ? browserPath(path) : undefined;
          })(),
          roundScores: (p.roundScores ?? []).map((rs) => ({
            round: rs.round,
            scoreCm: rs.scoreCm,
            isBullseye: rs.isBullseye,
            isDiscarded: rs.isDiscarded,
            isProvisional: rs.isProvisional,
          })),
        })),
      }));
  }, [category, competitionId, results]);

  const board = (
    <>
      {embedded ? <h2 className="sr-only">{title}</h2> : null}
      {isLoading ? (
          <div className="flex justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-sky-400 border-t-transparent" />
          </div>
        ) : category === 'TEAM' ? (
          teamEntries.length === 0 ? (
            <p className="text-muted-foreground">
              No rankings available yet. Approve or lock an official round, then recalculate rankings.
            </p>
          ) : (
            <div className="-mx-4 overflow-x-auto border-y border-border bg-card sm:mx-0 sm:rounded-xl sm:border">
              <TeamLeaderboard
                entries={teamEntries}
                roundNumbers={results?.roundNumbers}
                highlightPodium
                className="min-w-max [&_th]:h-8 [&_th]:border-border [&_th]:px-1 [&_th]:text-xs [&_th]:text-muted-foreground [&_td]:border-border/70 [&_td]:px-1 [&_td]:py-2 sm:[&_th]:px-3 sm:[&_td]:px-3 [&_tr]:border-border/70 [&_td]:text-foreground"
              />
            </div>
          )
        ) : entries.length === 0 ? (
          <p className="text-muted-foreground">
            No rankings available yet. Approve or lock an official round, then recalculate rankings.
          </p>
        ) : (
          <div className="-mx-4 overflow-x-auto border-y border-border bg-card sm:mx-0 sm:rounded-xl sm:border">
            <LeaderboardTable
              entries={entries}
              nameColumn={nameColumn}
              showBullseyes={showRoundScores ? false : showBullseyes}
              showRounds={!showRoundScores}
              showRoundScores={showRoundScores}
              roundNumbers={results?.roundNumbers}
              highlightPodium
              className="min-w-max [&_th]:h-8 [&_th]:border-border [&_th]:px-1 [&_th]:text-xs [&_th]:text-muted-foreground [&_td]:border-border/70 [&_td]:px-1 [&_td]:py-2 sm:[&_th]:px-3 sm:[&_td]:px-3"
            />
          </div>
        )}
    </>
  );

  if (embedded) return board;

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-6 py-12">
        <h1 className="mb-8 font-display text-4xl font-bold text-foreground">{title}</h1>
        {board}
      </div>
    </Layout>
  );
}

export function WomenBoard({ embedded = false }: { embedded?: boolean }) {
  return (
    <CategoryPage
      title="Women's Ranking"
      category="WOMEN"
      nameColumn="Pilot"
      showRoundScores
      embedded={embedded}
    />
  );
}

export function TeamsBoard({ embedded = false }: { embedded?: boolean }) {
  return (
    <CategoryPage
      title="Team Ranking"
      category="TEAM"
      nameColumn="Team"
      showBullseyes={false}
      embedded={embedded}
    />
  );
}

export function CountriesBoard({ embedded = false }: { embedded?: boolean }) {
  return (
    <CategoryPage
      title="Country Ranking"
      category="COUNTRY"
      nameColumn="Country"
      showBullseyes={false}
      embedded={embedded}
    />
  );
}
